import { Component } from '@theme/component';

/**
 * Filters FAQ questions by category. The filter buttons are built from the questions'
 * `data-category` / `data-category-label` attributes and stay hidden until this component
 * connects, so every question remains visible without JavaScript.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [filters] - Filter button group.
 * @property {HTMLElement} [status] - Live region announcing the visible question count.
 *
 * @extends Component<Refs>
 */
class CpFaqComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#buildFilters();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#buildFilters();
  }

  /**
   * Shows questions matching the clicked filter button.
   * @param {Event} event - Click event from a filter button.
   */
  filter(event) {
    const { target } = event;
    if (!(target instanceof HTMLButtonElement)) return;

    const key = target.dataset.filter ?? 'all';

    for (const button of this.refs.filters?.querySelectorAll('button') ?? []) {
      button.setAttribute('aria-pressed', String(button === target));
    }

    let visible = 0;
    for (const item of this.#items) {
      const show = key === 'all' || item.dataset.category === key;
      item.hidden = !show;
      if (show) visible += 1;
    }

    const { status } = this.refs;
    if (status) {
      status.textContent = (status.dataset.template ?? '').replace('[count]', String(visible));
    }
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.cp-faq-item')]);
  }

  #buildFilters() {
    const { filters } = this.refs;
    if (!filters) return;

    /** @type {Map<string, string>} */
    const categories = new Map();
    for (const item of this.#items) {
      const key = item.dataset.category;
      if (key && !categories.has(key)) categories.set(key, item.dataset.categoryLabel ?? key);
      item.hidden = false;
    }

    if (categories.size < 2) {
      filters.replaceChildren();
      filters.hidden = true;
      return;
    }

    const entries = [['all', filters.dataset.allLabel ?? 'All'], ...categories];
    filters.replaceChildren(
      ...entries.map(([key, label], index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cp-faq__filter';
        button.dataset.filter = key;
        button.textContent = label;
        button.setAttribute('aria-pressed', String(index === 0));
        button.setAttribute('on:click', '/filter');
        return button;
      })
    );
    filters.hidden = false;
  }
}

if (!customElements.get('cp-faq-component')) {
  customElements.define('cp-faq-component', CpFaqComponent);
}
